import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ReactPlayer from 'react-player'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { completeLesson } from '../../../actions/coachCertificationActions'
import { showGenericError } from '../../../utility/helperFunctions'
import { itemPath, nextItem, nextTarget, previousItem, sectionsPath, VIDEO_COMPLETION_THRESHOLD } from '../../../utility/coachCertification'
import ItemFooter from './ItemFooter'

import classes from './LessonView.module.scss'

const nextLabelKey = (next, target) => {
  if (!next) { return 'finish' }
  if (!target) { return 'sections' }
  if (target.type === 'lesson') { return 'lesson' }

  return target.final ? 'final' : 'quiz'
}

const LessonView = ({ item, items, sequential }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { lesson, sectionIndex, lessonIndex } = item
  const next = nextItem(items, item)
  const target = nextTarget(items, item)
  const previous = previousItem(items, item)
  const [isSaving, setIsSaving] = useState(false)
  const requested = useRef(false)
  const isVideo = !!lesson.vimeoEmbedUrl

  const complete = () => {
    if (lesson.completed || requested.current) { return Promise.resolve(true) }

    requested.current = true
    setIsSaving(true)
    return dispatch(completeLesson(lesson.id)).unwrap()
      .then(() => true)
      .catch(() => { requested.current = false; showGenericError(); return false })
      .finally(() => setIsSaving(false))
  }

  const handleProgress = ({ played }) => { if (played >= VIDEO_COMPLETION_THRESHOLD) { complete() } }

  const goNext = () => navigate(target ? itemPath(target) : sectionsPath(item.certificationId))

  const handleNext = () => (isVideo || lesson.completed ? goNext() : complete().then(saved => saved && goNext()))

  const nextDisabled = isSaving || (sequential && isVideo && !lesson.completed)
  const hintKey = isVideo ? (sequential ? 'unlocksAt' : 'completesAt') : 'markRead'
  const nextKey = isVideo || lesson.completed
    ? `portal.certification.item.next.${nextLabelKey(next, target)}`
    : 'portal.certification.item.doneReading'

  return (
    <div>
      {isVideo && (
        <div className={classes.video}>
          <ReactPlayer url={lesson.vimeoEmbedUrl} width='100%' height='100%' controls progressInterval={2000} onProgress={handleProgress} />
        </div>
      )}
      <div className={classNames(classes.body, { [classes.bodyCard]: !isVideo })}>
        <div className='d-flex align-items-baseline mb-2'>
          <span className={classes.eyebrow}>
            {t('portal.certification.item.lessonEyebrow', {
              section: sectionIndex + 1,
              lesson: lessonIndex + 1,
              kind: t(isVideo ? 'portal.certification.item.video' : 'portal.certification.item.reading'),
            })}
          </span>
        </div>
        <h1 className={classes.title}>{lesson.title}</h1>
        {lesson.body && <div className={classes.content}>{lesson.body}</div>}
        {!lesson.body && !isVideo && <div className={classes.noContent}>{t('portal.certification.item.noContent')}</div>}
      </div>
      <ItemFooter
        className={classes.footer}
        onPrevious={() => navigate(previous ? itemPath(previous) : sectionsPath(item.certificationId))}
        hint={lesson.completed ? null : t(`portal.certification.item.${hintKey}`)}
        nextLabel={t(nextKey)}
        onNext={handleNext}
        nextDisabled={nextDisabled}
        loading={isSaving}
      />
    </div>
  )
}

LessonView.propTypes = {
  item: PropTypes.shape({
    certificationId: PropTypes.number.isRequired,
    sectionIndex: PropTypes.number.isRequired,
    lessonIndex: PropTypes.number.isRequired,
    lesson: PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      body: PropTypes.string,
      vimeoEmbedUrl: PropTypes.string,
      completed: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  items: PropTypes.array.isRequired,
  sequential: PropTypes.bool.isRequired,
}

export default LessonView
